<template>
  <app-provider ref="appProvider">
    <template v-if="isLoggedIn == true && !isForbidden">
      <admin-shell
        :account="currentAccount"
        @sign-out="handleSignOut"
      >
        <router-view />
      </admin-shell>
    </template>

    <GlanceAuthLogin
      v-if="isLoggedIn == false"
      :auth="auth"
      :show-google="showGoogleAuth"
      :show-microsoft="showMicrosoftAuth"
      google-label="Sign In With Google"
      microsoft-label="Sign In With Microsoft"
      layout="stack"
    />

    <GlanceAuthForbidden
      v-if="isLoggedIn == true && isForbidden"
      :photo-url="auth.currentUser?.photoURL"
      :email="auth.currentUser?.email"
      message="You do not have permission to view this page.\nContact your manager."
      sign-out-label="Sign out"
      @sign-out="handleSignOut"
    />
  </app-provider>
</template>

<script>
import {
  getAuth,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth'
import { GlanceAuthLogin, GlanceAuthForbidden } from '@glanceai/common/ui'
import { useGlobalError } from '@/composables/useGlobalError'
import AdminShell from '@/components/AdminShell.vue'

export default {
  name: 'AppRoot',
  components: {
    GlanceAuthLogin,
    GlanceAuthForbidden,
    AdminShell,
  },
  setup() {
    const { isForbidden, resetForbidden } = useGlobalError()
    return { isForbidden, resetForbidden }
  },
  data() {
    return {
      isLoggedIn: null,
      auth: getAuth(),
      authProviders: {
        google: process.env.VUE_APP_AUTH_GOOGLE_ENABLED,
        microsoft: process.env.VUE_APP_AUTH_MICROSOFT_ENABLED,
      },
    }
  },
  computed: {
    showGoogleAuth() {
      return this.authProviders.google !== 'false'
    },
    showMicrosoftAuth() {
      return this.authProviders.microsoft === 'true'
    },
    currentAccount() {
      if (!this.isLoggedIn) {
        return null
      }
      const user = this.auth.currentUser
      if (!user) {
        return null
      }
      const provider = user.providerData?.[0]
      return {
        email: user.email || '',
        displayName: user.displayName || '',
        photoUrl: user.photoURL || '',
        emailVerified: Boolean(user.emailVerified),
        providerId: provider?.providerId || user.providerId || '',
      }
    },
  },
  provide() {
    return {
      auth: this.auth,
    }
  },
  methods: {
    handleSignOut() {
      signOut(this.auth).then(() => {
        this.isLoggedIn = false
        this.$router.push({ path: '/' })
      })
    },
  },
  mounted() {
    this.$api.auth = this.auth

    onAuthStateChanged(this.auth, (user) => {
      this.isLoggedIn = !!user
      this.$api.forbidden = false
      this.resetForbidden()

      if (this.isLoggedIn) {
        this.$nextTick(() => {
          this.$refs.appProvider?.prefetchSharedData?.()
        })
        if (this.$route.name == 'home') {
          this.$router.push({ name: 'props' })
        }
      }
    })
  },
}
</script>


<style>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html,
body,
#app {
  height: 100%;
  overflow: hidden;
}

body {
  font-family: "Segoe UI", "Helvetica Neue", Montserrat, sans-serif;
  font-size: 15px;
  color: var(--admin-text);
  background: var(--admin-bg);
}

.page-hero h1 {
  letter-spacing: -0.04em;
}

.close_icon,
.detail_icon,
.plus_icon,
.reload_icon,
.delete_icon,
.list_icon,
.edit_icon {
  width: 32px;
  height: 32px;
  cursor: pointer;
  opacity: .6;
  background: url('~@/assets/edit.png') no-repeat center / 90%;
}

.delete_icon {
  background-image: url('~@/assets/delete.png');
}

.plus_icon {
  background-image: url('~@/assets/plus.png');
}

.reload_icon {
  background-image: url('~@/assets/reload.png');
}

.detail_icon {
  width: 16px;
  height: 16px;
  margin-left: 5px;
  background-image: url('~@/assets/info.png');
}

.close_icon {
  width: 16px;
  height: 16px;
  margin-left: 5px;
  background-image: url('~@/assets/close.png');
}

@keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

.reload_icon.active {
  animation: spin 1s linear infinite;
}

.list_icon {
  background-image: url('~@/assets/list.png');
}

.close_icon:hover,
.detail_icon:hover,
.plus_icon:hover,
.reload_icon:hover,
.list_icon:hover,
.delete_icon:hover,
.edit_icon:hover {
  opacity: 1;
}

.btn {
  padding: 8px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  background: #fff;
  color: #111827;
  font-weight: 600;
  font-size: 13px;
}

.btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

*.noSelect {
  -moz-user-select: none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.gla-auth-login__stack .gla-auth-login__intro,
.gla-auth-login__stack .gla-auth-login__buttons {
  width: 100%;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}
</style>
